import { useFormikContext } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CERTIFICATE_IS_CA, CERTIFICATE_STATUS, CERTIFICATE_TYPES } from '../../constants/certificate-constants';
import { COMMAND_STATE } from '../../constants/command-status';
import Commands from '../../constants/commands';
import { AccountSubCmds } from '../../constants/sub-commands';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { ExecuteCommand } from '../../models/ExecuteCommand';
import { ListQuery } from '../../models/ListQuery';
import { executeCommand } from '../../services/executeCommandService';
import { getCertificateDetailsById, getCertificateNamesWithPagination } from '../../services/securityPolicyService';
import { getFormattedDateTime } from '../../services/utilities/dateFormatting';
import { getErrorMessage } from '../../services/utilities/ServiceError';
import { appendFilter } from '../../utilities/filters';
import ActivityFrame from '../activity-frame/activity-frame';
import { useDialog } from '../dialog/DialogContext';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';
import Tooltip from '../Tooltip/Tooltip';

interface AccountDetailProps {
    isLicensed: boolean,
    isAccountPopulated: boolean,
    currentItem: any,
}

export const VingcardAccountDetails: FC<AccountDetailProps> = ({ isLicensed, isAccountPopulated, currentItem }) => {
    const { translate } = useLocale();
    const { dirty } = useFormikContext<any>();
    const [lastSynced, setLastSynced] = useState<String>();
    const [busy, setBusy] = useState(false);
    const dialog = useDialog();
    const [vingCardCert, setVingcardCert] = useState<string>('');
    const { values, setFieldValue } = useFormikContext<any>();

    useEffect(() => {
        if (currentItem?.details ) {
            setSelectedItemLabel();
            setLastSynced(getFormattedDateTime(currentItem.details.lastSynced.value));
        }

    }, [currentItem, values.details.vingCardCert.value]);


    /**
    * The account is queried using saved account details only. Therefore, to query the account:
    * 1. Must be licensed.
    * 2. The account details must be populated.
    * 3. There must be no unsaved changes.
    * 4. The account must be enabled.
    * @returns
    */
    const canSyncAccount = () => {
        return isLicensed && isAccountPopulated && !dirty && currentItem?.enabled;
    }

    const setSelectedItemLabel = async () => {
        if (!values.details.vingCardCert.value) {
            return;
        }
        
        try {
            const vingCardCertName = (await getCertificateDetailsById(parseInt(values.details.vingCardCert.value), translate)).data.name;
            setVingcardCert(vingCardCertName);
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * For v1.5 only. Get the siteId, readerConfigurationId and avaialble credits in one endpoint call.
     * For future versions this needs to be queried separately so the user can select a site, etc.
     * @param event
     */
    const onSyncNow = (event) => {
        try {

            if (dirty) {
                dialog.pushError({ title: translate.byKey(TranslationKeys.unsavedChanges), message: translate.byKey(TranslationKeys.saveChangesBeforeTesting) });
                return;
            }

            setBusy(true);

            const command: ExecuteCommand = {
                cmd: Commands.Account.label,
                cmdSubType: AccountSubCmds.Sync_Vingcard_Now,
                entityId: "1",
                jsonResponse: ""
            }

            executeCommand(command).then((response: any) => {
                if (response.status === COMMAND_STATE.COMMAND_COMPLETE) {
                    toast.success(translate.byKey(TranslationKeys.successConnectedToAccount));
                }
                else {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.testAccountFailed) });
                }
            }).catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
            })
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        } finally {
            setBusy(false);
        }
    };

    // const errorHandler = (accessor) => {
    //     const error = accessor.split('.').reduce((previous, current) => { return previous?.[current] }, errors as any);
    //     if (error) {
    //         return <span className='text-red-600 text-xs'>{error}</span>
    //     } else {
    //         return null;
    //     }
    // };

    return <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>

        {/* Secure heading */}
        <div className="flex flex-col mt-3">
            <Label htmlFor='syncHeading' className="text-portal-blue-primary-shade2 text-md font-semibold">{translate.byKey(TranslationKeys.secureComms)}</Label>
        </div>

        <div className="flex flex-col mt-1">
            <Label required htmlFor='vingcardCertificateType' className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.vingcardCertificate)}</Label>
            <div className="mt-1 w-64">
                <PaginatedSearchableSelectField
                    name={translate.byKey(TranslationKeys.vingcardCertificate)}
                    key='vingcardCertificateType'
                    accessor='details.vingCardCert.value'
                    dataMethod={(listParameters?: ListQuery) => getCertificateNamesWithPagination({ ...listParameters,
                        filters: appendFilter(CERTIFICATE_IS_CA.NO, 'Certificate CA', 'certificateAuthority',
                        appendFilter(CERTIFICATE_TYPES.VINGCARD, 'Type', 'ksCertificateType.id',
                        appendFilter(CERTIFICATE_STATUS.REVOKED,'Certificate Status', 'ksCertificateStatus.id',listParameters?.filters ?? [], "=out="))) })}
                    searchAccessor={'trnName'}
                    required={false}
                    selectedItemLabel={vingCardCert}
                     onSelect={(_, value) => {
                        setFieldValue('details.vingCardCert.certName', value?.label);
                        setFieldValue('details.vingCardCert.value', value?.value);
                    }} />
            </div>
        </div>

        {/* Sync heading */}
        <div className="flex flex-col mt-4">
            <Label htmlFor='syncHeading' className="text-portal-blue-primary-shade2 text-md font-semibold">{translate.byKey(TranslationKeys.syncWithVingcard)}</Label>
            {lastSynced && <span className="text-sm text-gray-500">{translate.byKey(TranslationKeys.lastSynced)}: {lastSynced}</span>}
        </div>

        {/* Sync with Vingcard */}
        {<div className="flex w-full py-1">
            <button
                type='button'
                className={!canSyncAccount()
                    ? " secondary secondary-disabled cursor-not-allowed rounded-lg shadow-lg h-8 px-2 mt-1"
                    : " h-8 px-2 mt-1 secondary bg-sidemenu-focus text-white rounded-lg shadow-lg"}
                disabled={!canSyncAccount()}
                onClick={onSyncNow}
                id="syncButton">
                <Tooltip text={translate.byKey(TranslationKeys.syncWithVingcardTooltip)} direction='bottom' show={true}>
                    <span className="mr-2">{translate.byKey(TranslationKeys.syncNow)}</span>
                </Tooltip>
            </button>
        </div>}
    </ActivityFrame>
};
